import { LocateFixedIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';
import { toast } from 'sonner';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { FilterToolbar } from '@/components/organisms/FilterToolbar/FilterToolbar';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import useMembers from '@/hooks/useMembers';
import { useReportEmptyState } from '@/hooks/useReportEmptyState';
import { useSidebar } from '@/hooks/useSidebar';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { EVENT_NOTIFICATIONS_DOCS_URL } from '@/util/constants';

import { EmptyEventNotifications } from './EmptyEventNotifications';
import { PluginItem } from './PluginItem';
import {
  CREATOR_FILTER,
  STATUS_FILTER,
  creatorFilterDefinition,
  statusFilterDefinition,
} from '../../components/ResourceList/filterDefinitions';
import { ResourceList } from '../../components/ResourceList/ResourceList';
import { useEventOptions } from '../../hooks/useEventOptions';
import usePlugins from '../../hooks/usePlugins';
import { useResourceList } from '../../hooks/useResourceList';
import { Plugin } from '../../types';

const EVENT_FILTER = 'events';

interface PluginsProps {
  onEmptyStateChange: (isEmpty: boolean) => void;
}

export const Plugins = ({ onEmptyStateChange }: PluginsProps) => {
  const { t } = useTranslation();
  const { meta, isOpen, open } = useSidebar();
  const selectedId = isOpen('create_plugin') ? meta?.pluginId : undefined;
  const { plugins, listStatus, updatePlugin } = usePlugins();
  const { getMember } = useMembers();
  const eventOptions = useEventOptions();

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'plugins', actions.WRITE)
  );

  useTitle(t('pages.devtool_event_notifications'));

  const handleCreate = () => open('create_plugin');

  const handleEdit = (plugin: Plugin) => open('create_plugin', { pluginId: plugin.plugin_id });

  const handleToggleActive = (plugin: Plugin) =>
    updatePlugin(
      { id: plugin.plugin_id, data: { active: !plugin.active } },
      { onError: (error) => toast.error(error.message) }
    );

  const { visibleItems, toolbarProps } = useResourceList(plugins, {
    storageKey: 'eventNotificationsFilters',
    preferencesKey: 'event_notifications',
    filterValues: {
      [EVENT_FILTER]: (plugin) => plugin.events,
      [STATUS_FILTER]: (plugin) => (plugin.active ? 'active' : 'inactive'),
      [CREATOR_FILTER]: (plugin) => plugin.created_by,
    },
    // The tile doesn't show the URL, but search still matches it.
    searchText: (plugin) => [plugin.name, plugin.url],
  });

  const filterDefinitions = [
    {
      id: EVENT_FILTER,
      label: t('common.type'),
      icon: LocateFixedIcon,
      type: 'checkbox' as const,
      options: eventOptions.map(({ value, label }) => ({ value, label })),
    },
    statusFilterDefinition(t),
    creatorFilterDefinition(plugins, getMember, t),
  ];

  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'plugins',
    isLoading,
    isEmpty: plugins.length === 0,
    isError: listStatus === 'error',
  });

  useReportEmptyState(isEmptyPage, onEmptyStateChange);

  return (
    <div className="pt-5 pb-20">
      <ContextualHelp
        title={t('developer_tools.contextual_help.event_notifications_title')}
        name="about_event_notifications"
        links={[{ label: t('common.learn_more'), url: EVENT_NOTIFICATIONS_DOCS_URL }]}
      >
        {t('developer_tools.contextual_help.event_notifications_message')}
      </ContextualHelp>

      {showEmpty && <EmptyEventNotifications onCreate={handleCreate} canWrite={canWrite} />}

      {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

      {showList && (
        <>
          <FilterToolbar
            {...toolbarProps}
            searchPlaceholder={t('plugins.search_event')}
            filterDefinitions={filterDefinitions}
          />

          <ResourceList
            isLoading={isLoading}
            isEmpty={visibleItems.length === 0}
            noResultsMessage={t('developer_tools.event_notifications_no_results')}
          >
            {visibleItems.map((plugin) => (
              <PluginItem
                key={plugin.plugin_id}
                plugin={plugin}
                eventOptions={eventOptions}
                isSelected={plugin.plugin_id === selectedId}
                canWrite={canWrite}
                onEdit={handleEdit}
                onToggleActive={handleToggleActive}
              />
            ))}
          </ResourceList>
        </>
      )}
    </div>
  );
};
